import { useState } from "react";
import * as Sentry from "@sentry/react";
import * as api from "../../../../lib/claimsApi.ts";
import { isSentryWorthy } from "../../../../lib/errors";
import type { FeedbackFormData } from "../FeedbackModal";

export type FeedbackValue = 0 | 1 | 2 | 3;

interface UseFeedbackBarResult {
  selected: FeedbackValue | null;
  select: (value: FeedbackValue) => void;
  modalOpen: boolean;
  onModalClose: () => void;
  onModalSubmit: (data: FeedbackFormData) => Promise<void>;
}

export function useFeedbackBar(claimId: string): UseFeedbackBarResult {
  const [selected, setSelected] = useState<FeedbackValue | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  function persist(value: FeedbackValue) {
    setSelected(value);
  }

  function select(value: FeedbackValue) {
    persist(value);
    if (value <= 1) {
      // bad or meh — open feedback modal
      setModalOpen(true);
    } else {
      // good or great — submit positive feedback immediately
      api.submitFeedback(claimId, value, {}).catch((err) => {
        if (isSentryWorthy(err))
          Sentry.captureException(err, { extra: { endpoint: "/claims/feedback" } });
      });
    }
  }

  function onModalClose() {
    setModalOpen(false);
  }

  async function onModalSubmit(data: FeedbackFormData) {
    setModalOpen(false);
    const feedbackData: Record<string, unknown> = {
      reason: data.reason,
      details: data.details,
    };
    if (data.reason === "other" && data.otherText) {
      feedbackData.other_text = data.otherText;
    }
    try {
      await api.submitFeedback(claimId, selected ?? 0, feedbackData);
    } catch (err) {
      if (isSentryWorthy(err))
        Sentry.captureException(err, { extra: { endpoint: "/claims/feedback" } });
    }
  }

  return { selected, select, modalOpen, onModalClose, onModalSubmit };
}
